/**
 * The 86 drawing tools, in TradingView's own toolbar groups.
 *
 * Vendored verbatim from lightweight-charts-drawing (MIT, deepentropy),
 * demo/tool-groups.ts, which generates it from the drawing core. Copied
 * rather than imported because the library ships it only with its demo.
 * Regenerate by copying the file again on upgrade; do not hand-edit.
 */
export type ToolEntry = { kind: string; title: string; placement: string };
export type ToolGroup = { title: string; sections: { header: string | null; tools: ToolEntry[] }[] };
export const TOOL_COUNT = 86;
export const TOOL_GROUPS: ToolGroup[] = [
  { title: "Trend tools", sections: [
    { header: "Lines", tools: [
      { kind: "trend-line", title: "Trendline", placement: "2 clicks" },
      { kind: "ray", title: "Ray", placement: "2 clicks" },
      { kind: "info-line", title: "Info line", placement: "2 clicks" },
      { kind: "extended-line", title: "Extended line", placement: "2 clicks" },
      { kind: "trend-angle", title: "Trend angle", placement: "2 clicks" },
      { kind: "horizontal-line", title: "Horizontal line", placement: "1 click" },
      { kind: "horizontal-ray", title: "Horizontal ray", placement: "1 click" },
      { kind: "vertical-line", title: "Vertical line", placement: "1 click" },
      { kind: "cross-line", title: "Cross line", placement: "1 click" },
    ] },
    { header: "Channels", tools: [
      { kind: "parallel-channel", title: "Parallel channel", placement: "3 clicks" },
      { kind: "regression-trend", title: "Regression trend", placement: "2 clicks" },
      { kind: "flat-top-bottom", title: "Flat top/bottom", placement: "3 clicks" },
      { kind: "disjoint-channel", title: "Disjoint channel", placement: "3 clicks" },
    ] },
    { header: "Pitchforks", tools: [
      { kind: "pitchfork", title: "Pitchfork", placement: "3 clicks" },
      { kind: "schiff-pitchfork", title: "Schiff pitchfork", placement: "3 clicks" },
      { kind: "modified-schiff-pitchfork", title: "Modified Schiff pitchfork", placement: "3 clicks" },
      { kind: "inside-pitchfork", title: "Inside pitchfork", placement: "3 clicks" },
    ] },
  ] },
  { title: "Gann and Fibonacci tools", sections: [
    { header: "Fibonacci", tools: [
      { kind: "fib-retracement", title: "Fib retracement", placement: "2 clicks" },
      { kind: "trend-based-fib-extension", title: "Trend-based fib extension", placement: "3 clicks" },
      { kind: "fib-channel", title: "Fib channel", placement: "3 clicks" },
      { kind: "fib-time-zone", title: "Fib time zone", placement: "2 clicks" },
      { kind: "fib-speed-resistance-fan", title: "Fib speed resistance fan", placement: "2 clicks" },
      { kind: "trend-based-fib-time", title: "Trend-based fib time", placement: "3 clicks" },
      { kind: "fib-circles", title: "Fib circles", placement: "2 clicks" },
      { kind: "fib-spiral", title: "Fib spiral", placement: "2 clicks" },
      { kind: "fib-speed-resistance-arcs", title: "Fib speed resistance arcs", placement: "2 clicks" },
      { kind: "fib-wedge", title: "Fib wedge", placement: "3 clicks" },
      { kind: "pitchfan", title: "Pitchfan", placement: "3 clicks" },
    ] },
    { header: "Gann", tools: [
      { kind: "gann-box", title: "Gann box", placement: "2 clicks" },
      { kind: "gann-square-fixed", title: "Gann square fixed", placement: "2 clicks" },
      { kind: "gann-square", title: "Gann square", placement: "2 clicks" },
      { kind: "gann-fan", title: "Gann fan", placement: "2 clicks" },
    ] },
  ] },
  { title: "Patterns", sections: [
    { header: "Chart patterns", tools: [
      { kind: "xabcd-pattern", title: "XABCD pattern", placement: "5 clicks" },
      { kind: "cypher-pattern", title: "Cypher pattern", placement: "5 clicks" },
      { kind: "head-and-shoulders", title: "Head and shoulders", placement: "7 clicks" },
      { kind: "abcd-pattern", title: "ABCD pattern", placement: "4 clicks" },
      { kind: "triangle-pattern", title: "Triangle pattern", placement: "4 clicks" },
      { kind: "three-drives-pattern", title: "Three drives pattern", placement: "7 clicks" },
    ] },
    { header: "Elliott waves", tools: [
      { kind: "elliott-impulse", title: "Elliott impulse wave (1\u00b72\u00b73\u00b74\u00b75)", placement: "6 clicks" },
      { kind: "elliott-correction", title: "Elliott correction wave (A\u00b7B\u00b7C)", placement: "4 clicks" },
      { kind: "elliott-triangle", title: "Elliott triangle wave (A\u00b7B\u00b7C\u00b7D\u00b7E)", placement: "6 clicks" },
      { kind: "elliott-double-combo", title: "Elliott double combo wave (W\u00b7X\u00b7Y)", placement: "4 clicks" },
      { kind: "elliott-triple-combo", title: "Elliott triple combo wave (W\u00b7X\u00b7Y\u00b7X\u00b7Z)", placement: "6 clicks" },
    ] },
    { header: "Cycles", tools: [
      { kind: "cyclic-lines", title: "Cyclic lines", placement: "2 clicks" },
      { kind: "time-cycles", title: "Time cycles", placement: "2 clicks" },
      { kind: "sine-line", title: "Sine line", placement: "2 clicks" },
    ] },
  ] },
  { title: "Forecasting and measurement tools", sections: [
    { header: "Forecasting", tools: [
      { kind: "long-position", title: "Long position", placement: "1 click" },
      { kind: "short-position", title: "Short position", placement: "1 click" },
      { kind: "position-forecast", title: "Position forecast", placement: "2 clicks" },
      { kind: "bar-pattern", title: "Bars pattern", placement: "2 clicks" },
      { kind: "ghost-feed", title: "Ghost feed", placement: "clicks (min 2), double-click or click the last point to finish" },
      { kind: "sector", title: "Sector", placement: "3 clicks" },
    ] },
    { header: "Volume-based", tools: [
      { kind: "anchored-vwap", title: "Anchored VWAP", placement: "1 click" },
      { kind: "fixed-range-volume-profile", title: "Fixed range volume profile", placement: "2 clicks" },
      { kind: "anchored-volume-profile", title: "Anchored volume profile", placement: "1 click" },
    ] },
    { header: "Measurers", tools: [
      { kind: "price-range", title: "Price range", placement: "2 clicks" },
      { kind: "date-range", title: "Date range", placement: "2 clicks" },
      { kind: "date-and-price-range", title: "Date and price range", placement: "2 clicks" },
    ] },
  ] },
  { title: "Geometric shapes", sections: [
    { header: "Brushes", tools: [
      { kind: "brush", title: "Brush", placement: "press, drag, release" },
      { kind: "highlighter", title: "Highlighter", placement: "press, drag, release" },
    ] },
    { header: "Arrows", tools: [
      { kind: "arrow-marker", title: "Arrow marker", placement: "2 clicks" },
      { kind: "arrow", title: "Arrow", placement: "2 clicks" },
      { kind: "arrow-mark-up", title: "Arrow mark up", placement: "1 click" },
      { kind: "arrow-mark-down", title: "Arrow mark down", placement: "1 click" },
    ] },
    { header: "Shapes", tools: [
      { kind: "rectangle", title: "Rectangle", placement: "2 clicks" },
      { kind: "rotated-rectangle", title: "Rotated rectangle", placement: "3 clicks" },
      { kind: "path", title: "Path", placement: "clicks (min 2), double-click or click the last point to finish" },
      { kind: "circle", title: "Circle", placement: "2 clicks" },
      { kind: "ellipse", title: "Ellipse", placement: "3 clicks" },
      { kind: "polyline", title: "Polyline", placement: "clicks (min 2), double-click or click the last point to finish" },
      { kind: "triangle", title: "Triangle", placement: "3 clicks" },
      { kind: "arc", title: "Arc", placement: "3 clicks" },
      { kind: "curve", title: "Curve", placement: "2 clicks" },
      { kind: "double-curve", title: "Double curve", placement: "2 clicks" },
    ] },
  ] },
  { title: "Annotation tools", sections: [
    { header: "Text and notes", tools: [
      { kind: "text", title: "Text", placement: "1 click + text" },
      { kind: "note", title: "Note", placement: "2 clicks + text" },
      { kind: "price-note", title: "Price note", placement: "2 clicks + text" },
      { kind: "pin", title: "Pin", placement: "1 click + text" },
      { kind: "table", title: "Table", placement: "1 click" },
      { kind: "callout", title: "Callout", placement: "2 clicks + text" },
      { kind: "comment", title: "Comment", placement: "1 click + text" },
      { kind: "price-label", title: "Price label", placement: "1 click" },
      { kind: "signpost", title: "Signpost", placement: "1 click + text" },
      { kind: "flag-mark", title: "Flag mark", placement: "1 click" },
    ] },
    { header: "Content", tools: [
      { kind: "image", title: "Image", placement: "1 click" },
    ] },
  ] },
];
